import { Button, ButtonGroup, Divider, FormGroup, H3, H4, Tab, Tabs, Tree, Alignment } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IFieldProps } from "decorator-form";
import React from "react";
import styled, { css } from "styled-components";

import ArrayItemToolbar from "common/form/common/ArrayItemToolbar";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import CssUnitWidget from "common/form/widgets/CssUnitWidget";
import NumberWidget from "common/form/widgets/NumberWidget";
import VerticalCenter from "common/fragments/VerticalCenter";
import PositionWidget, { Position, positionToCss } from "common/form/widgets/PositionWidget";
import { arrayMove } from "core/util/ArrayUtils";
import { ILayoutType, LayoutTypeRegistry } from "../ILayoutType";
import { Separator } from "common/views/timeline/styled";
import { FlexColumns } from "common/fragments/Flex";

interface IGridRegion {
	row: number;
	column: number;
	rowSpan?: number;
	columnSpan?: number;
	regionUuid?: string;
	alignment?:  Position;
}

interface IGridLayout {
	columns: string[]; // units?
	rows: string[]; // units?
	regions: IGridRegion[];
	gap: string // units too?
}

interface IGridWrapperProps {
	layout: IGridLayout;
}

const GridWrapper = styled.div<IGridWrapperProps>(({layout}) => {

	const cols = layout.columns && layout.columns.join(' ') || undefined;
	const rows = layout.rows && layout.rows.join(' ') || undefined;

	return css`
		display: grid;
		display: -ms-grid;
		grid-template-columns: ${cols};
		-ms-grid-columns: ${cols};
		grid-template-rows: ${rows};
		-ms-grid-rows: ${rows};
		grid-gap: ${layout.gap};
		width: 100%;
	`;
});

interface IGridRegionProps {
	region: IGridRegion;
}

const GridItem = styled.div<IGridRegionProps>(({region}) => {

	const row = region.row || 1;
	const column = region.column || 1;
	const rspan = region.rowSpan || 1;
	const cspan = region.columnSpan || 1;

	return css`
		grid-row: ${row} / ${row + rspan};
		grid-column: ${column} / ${column + cspan};
		-ms-grid-row: ${row};
		-ms-grid-row-span: ${rspan};
		-ms-grid-column: ${column};
		-ms-grid-column-span: ${cspan};

		${positionToCss(region.alignment)}
	`;
});

interface IGridLayoutFieldProps extends IFieldProps<IGridLayout> {
	regions: { [uuid: string]: { __editName: string; }; };
}

class GridLayoutField extends React.Component<IGridLayoutFieldProps> {

	public render() {
		const { formData = {} as IGridLayout, regions } = this.props;

		return (
			<Tabs id="layout:grid">
				<Tab id="container" title="Container" panel={
					<React.Fragment>
						<FormGroup label="Gap / Spacing">
							<CssUnitWidget onChange={(gap) => this.partialChange({gap})} value={formData.gap} />
						</FormGroup>
						<Divider />
						<FormGroup label="Columns">
							{
								formData.columns && formData.columns.map((col, ci) => (
									<FormGroup key={ci}>
										<CssUnitWidget value={col} onChange={this.onChangeColumn(ci)} />
									</FormGroup>
								))
							}
							<FormGroup>
								<Button small={true} onClick={this.addColumn} text="Add Column" />
							</FormGroup>
						</FormGroup>
						<FormGroup label="Rows">
							{
								formData.rows && formData.rows.map((row, ri) => (
									<FormGroup key={ri}>
										<CssUnitWidget value={row} onChange={this.onChangeRow(ri)} />
									</FormGroup>
								))
							}
							<FormGroup>
								<Button small={true} onClick={this.addRow} text="Add Row" />
							</FormGroup>
						</FormGroup>
					</React.Fragment>
				} />
				<Tab id="areas" title="Grid Areas" panel={
					<FormGroup>
						{
							formData.regions && formData.regions.map((region, ri) => (
								<FormGroup key={ri}>
									<div style={{display: 'flex', justifyContent: 'space-between'}}>
										<H4>{`Region ${ri + 1}`}</H4>
										<ArrayItemToolbar arr={formData.regions} itemIndex={ri} onChange={this.onChangeRegions} />
									</div>
									<FormGroup label="Region">
										<DropdownSelect
											value={region.regionUuid}
											onChange={this.onChangeRegion(ri, 'regionUuid')}
											options={Object.entries(regions || {}).map(([uuid, reg]) => ({
												value: uuid,
												label: reg.__editName || "Untitled Region"
											}))}
										/>
									</FormGroup>
									<FlexColumns>
										<div style={{marginRight: "20px"}}>
											<FormGroup label="Column">
												<NumberWidget onChange={this.onChangeRegion(ri, 'column')} value={region.column} />
											</FormGroup>
											<FormGroup label="Row">
												<NumberWidget onChange={this.onChangeRegion(ri, 'row')} value={region.row} />
											</FormGroup>
											<FormGroup label="Column Span">
												<NumberWidget onChange={this.onChangeRegion(ri, 'columnSpan')} value={region.columnSpan} />
											</FormGroup>
											<FormGroup label="Row Span">
												<NumberWidget onChange={this.onChangeRegion(ri, 'rowSpan')} value={region.rowSpan} />
											</FormGroup>
										</div>
										<div>
											<FormGroup label="Child position">
												<PositionWidget onChange={this.onChangeRegion(ri, 'alignment')} value={region.alignment} />
											</FormGroup>
										</div>
									</FlexColumns>
								</FormGroup>
							))
						}
						<FormGroup>
							<Button small={true} onClick={this.addRegion} text="Add Grid Region" />
						</FormGroup>
					</FormGroup>
				} />
			</Tabs>
		);
	}

	private addColumn = () => {
		const { formData = {} as IGridLayout } = this.props;
		this.partialChange({
			columns: [...formData.columns || [], '1fr']
		});
	}

	private addRow = () => {
		const { formData = {} as IGridLayout } = this.props;
		this.partialChange({
			rows: [...formData.rows || [], '1fr']
		});
	}

	private onChangeColumn = (index: number) => (value: string) => {
		const { formData = {} as IGridLayout } = this.props;
		this.partialChange({
			columns: formData.columns.map((v, i) => i === index ? value : v)
		});
	}

	private onChangeRow = (index: number) => (value: string) => {
		const { formData = {} as IGridLayout } = this.props;
		this.partialChange({
			rows: formData.rows.map((v, i) => i === index ? value : v)
		});
	}

	private addRegion = () => {
		const { formData = {} as IGridLayout } = this.props;
		this.partialChange({
			regions: [...formData.regions || [], {row: 1, column: 1}]
		});
	}

	private onChangeRegion = (index: number, field: keyof IGridRegion) => (value: any) => {
		const { formData = {} as IGridLayout } = this.props;
		this.partialChange({
			regions: formData.regions.map((r, i) => i === index ? { ...r, [field]: value } : r)
		});
	}

	private onChangeRegions = (regions: IGridRegion[]) => this.partialChange({regions});

	private partialChange = (value: Partial<IGridLayout>) => {
		const { onChange, formData = {} as IGridLayout } = this.props;
		onChange({
			...formData,
			...value
		});
	}
}

const gridLayout: ILayoutType<IGridLayout> = {
	type: "grid",
	label: "Grid",
	icon: IconNames.GRID_VIEW,
	renderInput: (value, onChange, regions) => (
		<GridLayoutField
			regions={regions}
			onChange={onChange}
			formData={value}
			uiSchema={undefined}
			idSchema={undefined}
		/>
	),

	renderPreview: (value, regions) => {
		return (
			<GridWrapper layout={value} style={{minHeight: 200}}>
				{
					(value.columns || []).map((col, ci) => (value.rows || []).map((row, ri) => (
						<GridItem
							key={ri + '-' + ci}
							region={{column: ci + 1, row: ri + 1, columnSpan: 1, rowSpan: 1}}
							style={{backgroundColor: "rgba(0,0,0,0.2)", border: "1px solid rgba(0,0,0,0.3)", minHeight: "30px"}}
						/>
					)))
				}
				{
					value.regions && value.regions.map((region, ri) => (
						<GridItem key={ri} region={region} style={{backgroundColor: "rgba(19,124,189,0.2)", border: "1px solid rgba(19,124,189,0.3)", borderRadius: "4px", color: "rgb(19,124,189)"}}>
							<VerticalCenter style={{height: '100%'}}>
								{
									region.regionUuid ? (
										regions[region.regionUuid] && regions[region.regionUuid].__editName || "Invalid Region!"
									) : "(Select a Region)"
								}
							</VerticalCenter>
						</GridItem>
					))
				}
			</GridWrapper>
		);
	},

	renderLayout: (value, renderRegion) => {

		return (
			<GridWrapper layout={value}>
				{
					value.regions.map((region, ri) => (
						<GridItem key={ri} region={region}>
							{renderRegion(region.regionUuid, ri)}
						</GridItem>
					))
				}
			</GridWrapper>
		);
	}
};

export default LayoutTypeRegistry.registerType(gridLayout);



// WEBPACK FOOTER //
// ./src/common/form/fields/layout/layoutTypes/GridLayout.tsx